import { useSelector } from 'react-redux';

import { urls } from '@repo/app-constants';
import { IconName } from '@repo/ui';
import useCheckoutKit from 'hooks/useCheckoutKit';
import { useHasNewUpdate } from 'hooks/useNewUpdateIndicator';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import useSubscriptions from 'services/general/subscriptionsList';
import { getKitById } from 'store/kits';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { TrackEvent } from 'contexts/analytics/types';
import useGetMobilePartnerNotesItems from 'hooks/partnerNotes/mobile/useGetMobilePartnerNotesItems';
import useGetPartnerNotes from 'hooks/partnerNotes/useGetPartnerNotes';
import { PartnerNotesData } from 'store/partnerNotes';
import { getMobilePartnersRecsItems } from './helpers/getMobilePartnersRecsItems';
import { getPartnersRecsItemsDesktop } from './helpers/getPartnersRecsItems';
import styles from './Sidebar.module.css';

export type SidebarItemConfig = {
  to: string;
  icon: IconName;
  label: string;
  onClick?: () => void;
  state?: {
    tinyAccountId?: string | null;
    kitId?: string | null;
    from?: string | null;
    analyticsSource?: string;
  };
  external?: boolean;
  bottom?: boolean;
  hasKitId?: boolean;
  customClassName?: string;
  showDot?: boolean;
};
const MENU_ITEMS: {
  [key: string]: SidebarItemConfig;
} = {
  home: {
    to: '/',
    icon: 'home',
    label: 'Home',
  },
  results: {
    to: '/results/:kitId',
    icon: 'lightOn',
    label: 'Results',
    state: { analyticsSource: 'sidebar' },
  },
  actionPlan: {
    to: '/action-plan/:kitId',
    icon: 'today',
    label: 'Action plan',
    state: { analyticsSource: 'sidebar' },
  },
  kits: {
    to: '/kits',
    icon: 'add',
    label: 'Kits',
  },
  bookmarks: {
    to: '/bookmarks',
    icon: 'bookmark',
    label: 'Bookmarks',
  },
  resources: {
    to: '/guides',
    icon: 'book',
    label: 'Resources',
  },
  guides: {
    to: '/guides',
    icon: 'book',
    label: 'Resources',
  },
  store: {
    to: urls.STORE,
    icon: 'shoppingCart',
    label: 'Store',
    external: true,
  },
  consults: {
    to: '/consults',
    icon: 'messageHearthSquare',
    label: 'Coaching',
  },
  help: {
    to: '/help',
    icon: 'help',
    label: 'Need help?',
  },
  referAFriend: {
    to: '/refer-a-friend',
    icon: 'gift',
    label: 'Invite a friend',
  },
  facebook: {
    to: urls.FACEBOOK_GROUP,
    icon: 'userMultiple',
    label: 'Join our Facebook community',
    external: true,
  },
  account: {
    to: '/account',
    icon: 'userSingle',
    label: 'Account',
  },
};

const getHomePageSidebarItems = (
  kitId: string | null,
  tinyAccountId: string | null,
  storeSidebarItem: SidebarItemConfig,
  showStoreItem: boolean,
  showReferralItem: boolean,
  partnerNotesData: PartnerNotesData,
  trackEvent: TrackEvent,
): SidebarItemConfig[] => {
  const items = [
    MENU_ITEMS.home,
    MENU_ITEMS.results,
    MENU_ITEMS.actionPlan,
    ...getPartnersRecsItemsDesktop(partnerNotesData, trackEvent),
    MENU_ITEMS.resources,
    { ...MENU_ITEMS.bookmarks },
    { ...MENU_ITEMS.account, bottom: true },
    { ...MENU_ITEMS.help, bottom: true },
  ];

  // Add referral item if not hidden by partner config
  if (showReferralItem) {
    items.push({ ...MENU_ITEMS.referAFriend, bottom: true });
  }

  // Add store item if not hidden by partner config
  if (showStoreItem) {
    items.splice(6, 0, { ...storeSidebarItem, bottom: true });
  }

  return items.map(item => ({
    ...item,
    to: item.to.replace(':kitId', kitId ?? ''),
    state: { tinyAccountId, ...item.state },
  }));
};

const decorateWithDots = (
  items: SidebarItemConfig[],
  dots: { results: boolean; actionPlan: boolean },
): SidebarItemConfig[] =>
  items.map(item => {
    if (item === MENU_ITEMS.results || item.label === MENU_ITEMS.results.label) {
      return { ...item, showDot: dots.results };
    }
    if (
      item === MENU_ITEMS.actionPlan ||
      item.label === MENU_ITEMS.actionPlan.label
    ) {
      return { ...item, showDot: dots.actionPlan };
    }
    return item;
  });
const getMobileSidebarItems = (
  storeSidebarItem: SidebarItemConfig,
  showStoreItem: boolean,
  showReferralItem: boolean,
  mobilePartnerNotes: PartnerNotesData[string][],
  trackEvent: TrackEvent,
): SidebarItemConfig[] => {
  const items = [
    MENU_ITEMS.account,
    MENU_ITEMS.guides,
    ...getMobilePartnersRecsItems(mobilePartnerNotes, trackEvent),
    MENU_ITEMS.bookmarks,
  ];

  // Add referral item if not hidden by partner config
  if (showReferralItem) {
    items.push(MENU_ITEMS.referAFriend);
  }

  items.push(MENU_ITEMS.facebook);
  items.push(MENU_ITEMS.help);

  // Add store item if not hidden by partner config
  if (showStoreItem) {
    items.splice(2, 0, storeSidebarItem);
  }

  return items;
};
export const useSidebarItems = ({
  kitId,
  isMobile = false,
}: {
  kitId?: string;
  isMobile?: boolean;
}) => {
  const kit = useSelector(getKitById(kitId));
  const tinyAccountId = kit?.tinyaccount_id;

  const partnerNotesData = useGetPartnerNotes();
  const [_, ...mobilePartnerNotes] =
    useGetMobilePartnerNotesItems(partnerNotesData);
  const { trackEvent } = useAnalytics();

  const { isAnyTinyPlus } = useSubscriptions();
  const { checkoutUrl } = useCheckoutKit({
    kitType: kit?.sampling_type,
    nonMemberUrl: urls.STORE,
  });
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs();

  const hideUpsells =
    Boolean(portalConfig.hide_upsells) || isPortalConfigLoading;

  // Hide store if partner config says so
  const showStoreItem = !hideUpsells;
  // Hide referral if partner config says so (referral is store-related)
  const showReferralItem = !hideUpsells;

  const storeSidebarItem: SidebarItemConfig = {
    to: checkoutUrl,
    icon: 'shoppingCart',
    label: isAnyTinyPlus ? 'Tiny+ store' : 'Store',
    external: true,
    bottom: true,
    onClick: () =>
      trackEvent('Store Viewed', {
        source: isMobile ? 'mobile_menu' : 'sidebar',
      }),
  };

  const navKitId = kitId ?? null;
  const { hasNewUpdate: resultsHasNewUpdate } = useHasNewUpdate(
    tinyAccountId ?? null,
    navKitId,
    'results',
  );
  const { hasNewUpdate: actionPlanHasNewUpdate } = useHasNewUpdate(
    tinyAccountId ?? null,
    navKitId,
    'actionPlan',
  );

  if (isMobile) {
    return getMobileSidebarItems(
      storeSidebarItem,
      showStoreItem,
      showReferralItem,
      mobilePartnerNotes,
      trackEvent,
    );
  }

  const homeItems = getHomePageSidebarItems(
    kitId ?? null,
    tinyAccountId ?? null,
    storeSidebarItem,
    showStoreItem,
    showReferralItem,
    partnerNotesData,
    trackEvent,
  );
  return decorateWithDots(homeItems, {
    results: resultsHasNewUpdate,
    actionPlan: actionPlanHasNewUpdate,
  });
};
